/**
 * Apps + tabs — single source of truth voor het framework chrome (S010.2).
 *
 * Productie-actief in V1: alleen "Glas" en "Inrichting". De 7 andere apps
 * worden zichtbaar in de app-switcher popover als "Binnenkort" tiles,
 * zonder route-target. Disabled tabs binnen een app krijgen dezelfde
 * "Binnenkort"-treatment in de tab-strip.
 *
 * Active-app + active-tab matching gaat via `pathname.startsWith(tab.to)`.
 * Detail-routes (`/dossiers/:id`, `/artikelbeheer/prijslijsten/:id/bewerken`,
 * …) houden de parent-tab actief — geen exact-match nodig.
 *
 * Systeem → Menu (story `be-fe-menu-aan-uit`) toont deze lijst en laat System elke app en elke tab aan of
 * uit zetten (sleutel `menu.<app-id>[.<tab-id>]`); AppSwitcher, AppTabs en de route-guard lezen dat via
 * `@/auth/menuVisibility`. Een nieuwe app of tab staat daar dus vanzelf in — houd de `id`'s stabiel.
 *
 * Bewust géén `defaultRoute: '#'` voor disabled apps: we gebruiken een
 * sentinel `null` voor `defaultRoute` zodat TS de niet-navigeerbare staat
 * zelf afdwingt en click-handlers expliciet moeten branchen.
 */
import {
    BarChart3,
    Car,
    Cog,
    FileText,
    Filter,
    GraduationCap,
    LifeBuoy,
    Package,
    PhoneCall,
    Receipt,
    Repeat,
    ServerCog,
    ShieldCheck,
    ShoppingCart,
    Users,
    type LucideIcon,
} from 'lucide-react';

import { CLAIMFILTER_AUDIENCE_ROLES, CLAIMFILTER_ROUTES } from '@/auth/claimFilterAudience';
import { VERZEKERAAR_ROLE } from '@/auth/roles';

export interface Tab {
    /** Stabiele identifier — gebruikt als React-key en in tests. */
    id: string;
    /** UI-label (Nederlands). */
    label: string;
    /** Route-prefix; `startsWith(to)` bepaalt active-state. */
    to: string;
    /** Klikbaar + route-target aanwezig? `false` → "Binnenkort". */
    enabled: boolean;
    /**
     * Optionele rol-eis: de tab is alleen zichtbaar wanneer de ingelogde user
     * minstens ÉÉN van deze rol-claims heeft (multi-rol, 2026-06-06 —
     * controleredenen = `glasadmin` OF `hoofdkantoor`). De route zelf is
     * daarnaast hard gegate via `<ProtectedRoute roles>`.
     */
    requiresAnyRole?: string[];
    /**
     * Optionele context-eis: de tab is alleen zichtbaar wanneer de ingelogde user
     * een **System**-gebruiker is (`userContextType === 'System'`). Spiegelt
     * `App.requiresSystemContext` maar op tab-niveau — voor een system-only
     * beheer-tab binnen een verder open app (story `rapportage-per-rol-fase1`:
     * "Rapportage-menu per rol" onder Inrichting). De route is daarnaast hard
     * gegate via `<ProtectedRoute systemOnly>`.
     */
    requiresSystemContext?: boolean;
    /**
     * Optionele context-eis: de tab is alleen zichtbaar wanneer de ingelogde user
     * een **hersteller** is (`userContextType ∈ {RepairerHQ, RepairerLocation}`).
     * Voor een hersteller-only functie binnen een verder open app (story
     * `grootboeken-hersteller-only`: de Grootboeken-tab van Facturatie mag NIET
     * aan System/RegularUser worden getoond, want de bootstrap-GET zaait dan
     * grootboeken voor de verkeerde company). Spiegelt `requiresSystemContext`
     * maar op hersteller-context. De route is daarnaast hard gegate via
     * `<ProtectedRoute repairersOnly>`.
     */
    requiresRepairerContext?: boolean;
    /**
     * Story `fe-be-verzekeraar-gebonden-hersteller-chrome-tabs` (EPIC-019 S-C,
     * 2026-08-21): de tab is **niet aanwezig in de DOM** zodra de eigen sessie een
     * verzekeraar-gebonden (beperkte) hersteller is
     * (`isRestrictedRepairerActive(user) === true`, module
     * `@/auth/restrictedRepairer`). Verbergen = **niet renderen** — nooit een
     * `<button disabled>`, nooit "Binnenkort", nooit `aria-hidden`
     * (CLAUDE.md: "navigatie-items zijn echte links").
     *
     * Componeert **AND** met de andere gate-velden en keyt uitsluitend op de
     * capability van de eigen sessie — nooit op de omgevings-schakelaar of de
     * instantie (op één instantie werken beperkte én gewone herstellers naast
     * elkaar). Bij `resolved: false` ("onbekend") verandert er niets.
     */
    hiddenWhenRestrictedRepairer?: boolean;
    /**
     * Story `fe-be-verzekeraar-gebonden-hersteller-chrome-tabs` (EPIC-019 S-C,
     * 2026-08-21): de tab is bij een **beperkte** hersteller alleen zichtbaar
     * wanneer `mayUseScratchpad === true`; bij een **niet-beperkte** hersteller
     * heeft dit veld **geen enkel effect** (het kladblok is dan gewoon
     * beschikbaar — AC 3/AC 8).
     *
     * Staat op exact één entry: de Glas-tab `calculatie` (het **losse** kladblok).
     * Bewust NIET samen met {@link hiddenWhenRestrictedRepairer} — dat zou het
     * menu-item óók bij `mayUseScratchpad === true` weghalen.
     */
    requiresScratchpadWhenRestricted?: boolean;
    /**
     * 30-09-2026: de tab is alleen zichtbaar wanneer de omgevings-schakelaar
     * `Intakes.Enabled` in `AppServices\Web.config` aan staat
     * (`user.intakesEnabled === true`). Niet elke instantie heeft een
     * callcenter-instroom; waar die er niet is hoort het menu-item er ook niet
     * te staan.
     *
     * Componeert **AND** met de andere gate-velden. De schakelaar is
     * **fail-closed**: ontbreekt de sleutel, dan is de tab weg — zie de
     * BE-uitleg in `IntakesFeatureSwitch`.
     *
     * ⚠ Dit is GEEN slot. De route `/intakes` blijft bereikbaar via een
     * bookmark, de endpoints blijven antwoorden en ⌘K toont de groep
     * "Meldingen" gewoon (keuze gebruiker 30-09-2026: alleen het menu-item).
     * Zet er dus nooit een autorisatie-redenering op.
     */
    requiresIntakesEnabled?: boolean;
    /**
     * Story `be-fe-relatiebeheer-herstellers` (HB, F.1): de tab is alleen zichtbaar
     * wanneer whoami `canManageRepairers === true` meldt (System of het
     * keten-hoofdkantoor). Componeert **AND** met de andere gate-velden.
     *
     * ⚠ Dit is een menu-hint, geen slot: de route blijft via een bookmark bereikbaar en
     * toont dan het juiste foutscherm (403/404/503 van `api/repairers`). De autoriteit is
     * de backend (`RepairerAdminAccess`), nooit een rolcheck in de FE.
     */
    requiresCanManageRepairers?: boolean;

    /**
     * Story `be-fe-meerprijs-dealer-kortingen` (AC 11.1): de tab is alleen zichtbaar wanneer whoami
     * `canManageDealerSurcharge === true` meldt (vlag `DealerSurcharge.Enabled` aan én System of het keten-hoofdkantoor).
     * Fail-closed op `=== true`. Een menu-hint, geen slot: beide routes hangen achter
     * `<ProtectedRoute canManageDealerSurcharge>` en elk `api/dealer-surcharge`-endpoint beslist zelf.
     */
    requiresCanManageDealerSurcharge?: boolean;

    /**
     * Story `be-fe-support-app-behandelen` (S2, AC 3): de tab is alleen zichtbaar wanneer whoami
     * `canHandleSupport === true` meldt (System of het keten-hoofdkantoor). Fail-closed op `=== true`. Staat ook op
     * {@link App.requiresCanHandleSupport}: de app-switcher kijkt alleen naar app-velden.
     *
     * ⚠ Een menu-hint, geen slot: de route toont zelf het foutscherm (403/503 van `api/support/inbox`).
     */
    requiresCanHandleSupport?: boolean;

    /**
     * Story `be-fe-systeem-gebruikers` (AC 4): de tab is alleen zichtbaar wanneer whoami `canManageUsers === true` meldt
     * (System of het keten-hoofdkantoor). Fail-closed op `=== true`. Een menu-hint, geen slot: de route hangt achter
     * `<ProtectedRoute canManageUsers>` en elk `api/system/users`-endpoint beslist zelf (403/404/503).
     */
    requiresCanManageUsers?: boolean;

    /**
     * Story `be-fe-facturatie-run-controle` (R1, PB-R1-8): de tab is alleen zichtbaar wanneer
     * `GET /api/facturatie/billing-runs/options` 200 gaf (de gebruiker hoort bij het bedrijf dat eigenaar is van
     * abonnementen, of is `system`). 403 en 503 ⇒ weg; nog onbekend ⇒ weg (fail-closed). Eén probe per sessie
     * (`features/billingRuns/billingRunsAccess.ts`), geen veld op het hete whoami-pad.
     *
     * ⚠ Een menu-hint, geen slot: de route blijft bereikbaar en toont zelf "Geen toegang" of "Kon niet bepalen
     * of je runs mag bekijken". De autoriteit is de backend.
     */
    requiresBillingRunsAccess?: boolean;

    /**
     * EPIC-022 S022.1 (AC 19): de tab is klikbaar (echte `<Link>`) zodra whoami `inventoryInCarculate === true` meldt,
     * ook al staat `enabled` op `false`. Voor ieder ander blijft hij exact zoals nu ("Binnenkort", niet klikbaar).
     * Componeert met de andere poorten: een tab die in Systeem → Menu uit staat blijft verborgen (menu wint).
     * Lezen uitsluitend via `effectiveTabEnabled` (`@/auth/inventory`).
     */
    enabledWhenInventoryInCarculate?: boolean;
}

/**
 * Story `fe-tab-groepen-en-meer-menu` (24-10-2026, concept `menu-te-breed-v1.html` deel 4): een groep tabs die in de
 * tab-strip als één knop "Label ▾" met een uitklapmenu verschijnt (`AppTabs`, `tabStrip.ts`).
 *
 * Bewust een lijst op de APP en geen `group`-veld per tab: de tabs zelf (id, label, route, poorten) blijven
 * ongewijzigd — ze zijn de sleutels van Systeem → Menu (`menu.<appId>.<tabId>`) — en label + volgorde van de groep
 * staan op één plek.
 *
 * - Elke poort geldt eerst PER TAB; een groep toont alleen haar zichtbare tabs, en een groep zonder zichtbare tab
 *   bestaat niet. Een groep met precies één zichtbare tab wordt een gewone tab.
 * - De groep staat in de strip op de plek van haar eerste tab in {@link App.tabs}; binnen de groep geldt de volgorde
 *   van {@link tabIds}.
 * - Een tab hoort bij hooguit één groep, en elk id bestaat in `tabs` (`apps.config.tabGroups.test.ts`).
 */
export interface TabGroup {
    /** Stabiele identifier (React-key, tests). Geen menu-sleutel: Systeem → Menu kent alleen apps en tabs. */
    id: string;
    /** UI-label van de groepsknop (Nederlands). */
    label: string;
    /** Geordende tab-id's van deze app die in de groep vallen. */
    tabIds: string[];
}

export interface App {
    /** Stabiele identifier — gebruikt als key + in tests. */
    id: string;
    /** UI-label (Nederlands). */
    label: string;
    /** Lucide-icoon voor de app-switcher tile + chip-icoon. */
    icon: LucideIcon;
    /**
     * Default-route bij klik op enabled-tile of logo. `null` voor disabled
     * apps zodat de click-handler de "Binnenkort"-tak kiest.
     */
    defaultRoute: string | null;
    /** App actief in V1? Disabled apps verschijnen wel in de switcher. */
    enabled: boolean;
    /**
     * Optionele context-eis: de app is alleen zichtbaar/bereikbaar wanneer de
     * ingelogde user een **System**-gebruiker is (`userContextType === 'System'`).
     * Spiegelt de `Tab.requiresAnyRole`-aanpak, maar gate't op het
     * context-type i.p.v. op rol-claims. `AppSwitcher` filtert de tile weg voor
     * niet-system-gebruikers (ook geen "Binnenkort"); de routes zijn daarnaast
     * hard gegate via `<ProtectedRoute systemOnly>`. Bewust een boolean en geen
     * generieke `requiresAnyContextType?: string[]`: het enige onderscheid in V1
     * is system vs. niet-system, dus de boolean houdt config + gate-logica
     * simpel en intentie-helder.
     */
    requiresSystemContext?: boolean;
    /**
     * Story `fe-be-verzekeraar-rol-scope-worklist` (2026-06-29): de app is alleen
     * zichtbaar/relevant wanneer de ingelogde user minstens ÉÉN van deze
     * rol-claims heeft. Gebruikt voor de aparte verzekeraar-app (kale chrome) —
     * spiegelt `Tab.requiresAnyRole` maar op app-niveau. De Chrome gate't de
     * volledige kale-modus hierop; de route is daarnaast hard gegate via
     * `<ProtectedRoute roles>`.
     */
    requiresAnyRole?: string[];
    /**
     * Story `fe-be-verzekeraar-gebonden-hersteller-chrome-tabs` (EPIC-019 S-C,
     * 2026-08-21): de app-tegel is **niet aanwezig in de DOM** zodra de eigen
     * sessie een verzekeraar-gebonden (beperkte) hersteller is
     * (`isRestrictedRepairerActive(user) === true`, module
     * `@/auth/restrictedRepairer`) — ook niet als "Binnenkort"-tile.
     *
     * De definitieve lijst komt uit **beslissing B1** (gebruiker, 2026-08-21):
     * weg = Inkoop · Facturatie · Inrichting · Artikelbeheer · Rapportage;
     * blijft = Glas · Relatiebeheer · Documentatie. `verzekeraar` en `system`
     * zitten al achter een rol-/context-gate en worden hier niet geraakt.
     *
     * Deze vlag voedt óók de route-prefix-lijst van
     * `RestrictedRepairerRouteGuard` (beslissing C1) — één bron van waarheid voor
     * "welke apps bestaan niet voor deze gebruiker".
     */
    hiddenWhenRestrictedRepairer?: boolean;
    /**
     * Story `be-fe-verzekeraar-alleen-lezen-en-facturatie-verbergen` (22-10-2026, B-4 / AC 7): de app is verborgen
     * (tegel, tab-strip, route-guard) wanneer whoami meldt dat facturatie uit staat — de hersteller van de
     * gebruiker heeft facturatie niet aan. Afwezig of `true` ⇒ zichtbaar. Gelezen uitsluitend via
     * `@/auth/invoicingActive` en meegenomen in `isAppHiddenByMenu` (één classificatie).
     *
     * Staat op exact één app: `facturatie`. "Interne facturatie" valt er bewust niet onder. Een menu-hint, geen slot.
     */
    hiddenWhenInvoicingInactive?: boolean;
    /**
     * Story `fe-be-claimfilter-beheerscherm` (2026-08-31, AC-5.1) — het
     * **declaratieve OF-veld**. De app is zichtbaar/bereikbaar wanneer de
     * ingelogde user **System**-context heeft **ÓF** minstens één van deze
     * rol-claims draagt.
     *
     * ⚠⚠ **Dit veld bestaat omdat de andere twee met EN componeren.**
     * `requiresSystemContext` en `requiresAnyRole` worden in `AppSwitcher`
     * (`(!app.requiresSystemContext || isSystemUser) && (!app.requiresAnyRole || …)`)
     * en in `AppTabs` met **&&** samengevoegd. Wie ze allebei op één app zet,
     * krijgt een tegel die alleen zichtbaar is voor iemand met system-context
     * **én** die rol — dus voor niemand. Er komt geen foutmelding uit; er
     * verschijnt alleen nooit een tegel, en dat is van buiten niet te
     * onderscheiden van "nog niet gebouwd".
     *
     * ⚠ Dit veld is daarom **onverenigbaar** met {@link requiresSystemContext}
     * en {@link requiresAnyRole}: zet ze nooit samen op één app. Geborgd met
     * `apps.config.claimfilter.test.ts`.
     *
     * ⚠ De vier tabs van zo'n app **erven** deze poort en dragen **géén eigen
     * gate** — anders wordt dezelfde EN-fout één niveau lager vier keer
     * herhaald, en dan is de tegel wél zichtbaar maar zijn de tabs leeg (AC-5.2).
     *
     * De poortlogica zelf staat op één plek: `maySeeWithSystemOrAnyRole` in
     * `@/auth/claimFilterAudience`. `AppSwitcher`, `AppTabs` én `ProtectedRoute`
     * lezen alle drie díe functie — nooit een `app.id === '…'`-vergelijking.
     */
    requiresSystemContextOrAnyRole?: readonly string[];

    /**
     * Story `be-fe-systeem-gebruikers` (P-2, AC 4) — de **OF**-poort van de app System: zichtbaar bij System-context
     * **ÓF** whoami `canManageUsers === true` (het keten-hoofdkantoor ziet de tegel System met alleen *Gebruikers*).
     *
     * ⚠⚠ Onverenigbaar met {@link requiresSystemContext} (die componeert met EN): zet ze nooit samen op één app. Omdat de
     * app-poort hierdoor ruimer is, draagt **elke tab** van zo'n app zijn eigen poort — `requiresSystemContext: true`,
     * of `requiresCanManageUsers: true` voor *Gebruikers*. Een tab zonder poort is rood in
     * `features/system/users/__tests__/systemUsersApp.test.ts`.
     */
    requiresSystemContextOrCanManageUsers?: boolean;

    /**
     * Story `be-fe-callcenter-melding-aanmaken` (10-10-2026): de tegel is alleen
     * zichtbaar wanneer de omgevings-schakelaar `Intakes.Enabled` aanstaat
     * (whoami `intakesEnabled === true`).
     *
     * ⚠ **Dit veld bestaat ook op {@link Tab}, en dat is geen dubbeling.** De
     * app-switcher filtert **uitsluitend** op app-velden en kijkt nooit naar
     * tabs; een app waarvan álle tabs verborgen zijn houdt gewoon zijn tegel en
     * leidt dan naar een lege tab-strip. Hoort de tegel aan dezelfde schakelaar
     * te hangen als de tabs, dan moet de poort op beide niveaus staan.
     *
     * Fail-closed op `=== true`, net als de tab-variant: `undefined`, `null`,
     * `'true'` en `1` tellen allemaal als UIT.
     */
    requiresIntakesEnabled?: boolean;

    /**
     * Besluit eigenaar 10-10-2026: de app "Interne facturatie" (facturatie-runs) is alleen zichtbaar bij
     * bevestigde toegang — `GET /api/facturatie/billing-runs/options` gaf 200 (`'allowed'`). 403, 503 en
     * "nog onbekend" ⇒ geen tegel en geen tab-strip (fail-closed). Dezelfde probe als de tab-variant
     * ({@link Tab.requiresBillingRunsAccess}, `features/billingRuns/billingRunsAccess.ts`).
     *
     * ⚠ Staat op app-niveau om dezelfde reden als `requiresIntakesEnabled`: de app-switcher kijkt alleen naar
     * app-velden. Een menu-hint, geen slot: de route toont zelf "Geen toegang".
     */
    requiresBillingRunsAccess?: boolean;

    /**
     * Story `fe-be-cursusmodus-hersteller-mvp` (AC-C1.1): de app is alleen zichtbaar wanneer
     * `canUseCourses(user)` (`@/auth/trainingMode`) — een hersteller-vestiging op een actieve
     * trainingsomgeving. Bij elke andere combinatie is de tegel niet in de DOM (ook niet als
     * "Binnenkort") en rendert de tab-strip niet (erfregel in `AppTabs`).
     *
     * ⚠ `AppSwitcher` en `AppTabs` passen dit veld UITSLUITEND via `canUseCourses(` toe — nooit
     * via een `app.id`-vergelijking of een eigen hersteller-toets (scan TC-C1-03c/TC-C1-05).
     */
    requiresCourses?: boolean;

    /**
     * Story `be-fe-support-app-behandelen` (S2, AC 3): de tegel is alleen zichtbaar bij whoami
     * `canHandleSupport === true`. Fail-closed. Staat op app- én tab-niveau (zelfde reden als
     * `requiresIntakesEnabled`). ⚠ Bewust GEEN `hiddenWhenRestrictedRepairer`: die voedt de route-prefix-lijst van
     * `RestrictedRepairerRouteGuard` en zou `/support/requests` (S1, melder) voor een gebonden hersteller blokkeren.
     */
    requiresCanHandleSupport?: boolean;

    /**
     * Story `be-fe-support-app-behandelen` (S2): het eerste padsegment van deze app wordt gedeeld met routes die
     * NIET bij de app horen (`/support/inbox` naast de melder-routes `/support/requests` van S1). Staat de app in
     * Systeem → Menu uit, dan blokkeert de padclassificatie alleen de eigen tab-paden en niet het hele segment —
     * anders zou "Support uit" ook *Mijn supportmeldingen* dichtzetten.
     */
    sharedFirstSegment?: boolean;

    /** Tab-strip onder de header — leeg voor disabled apps. */
    tabs: Tab[];

    /** Optionele groepering van de tabs in de strip ({@link TabGroup}). Zonder groepen rendert de strip plat. */
    tabGroups?: TabGroup[];
}

/**
 * 9 apps conform refined story §"Apps + tabs config". Volgorde = volgorde
 * in de app-switcher popover (2-koloms grid, rij-voor-rij).
 */
export const APPS: App[] = [
    {
        id: 'glas',
        label: 'Glas',
        icon: Car,
        defaultRoute: '/dashboard',
        enabled: true,
        tabs: [
            { id: 'dashboard',    label: 'Dashboard',    to: '/dashboard',    enabled: true  },
            { id: 'dossiers',     label: 'Dossiers',     to: '/dossiers',     enabled: true  },
            // Story fe-calculatie-kladblok-glas (2026-06-23): losse calculatie-
            // pagina ("kladblok") — snel prijzen bepalen zonder dossier. Echte
            // <Link> (config-driven via AppTabs) → rechtsklik-nieuw-tab werkt.
            // S-C (2026-08-21, B1): voor een BEPERKTE hersteller alleen zichtbaar
            // met de kladblok-vlag. Bewust GEEN hiddenWhenRestrictedRepairer —
            // dat zou het item ook bij `mayUseScratchpad: true` weghalen. De
            // Calculatie-TAB in het dossier is iets anders en blijft altijd staan.
            { id: 'calculatie',   label: 'Calculatie',   to: '/calculatie',   enabled: true, requiresScratchpadWhenRestricted: true },
            // S-C (2026-08-21, B1): Nieuwsberichten BLIJFT voor de beperkte
            // hersteller — een informatie-item, geen module.
            //
            // Meldingen (2026-08-23, PO-bijstelling op B1): vervalt alsnog voor de
            // beperkte hersteller. De meldingen-instroom is de accepteer/weiger-rail
            // van een hersteller die zelf werk binnenkrijgt; een verzekeraar-gebonden
            // hersteller krijgt zijn werk via de vaste verzekeraar en heeft er niets
            // te doen. Loopt via ditzelfde config-veld als de rest — geen uitzondering
            // ernaast — en voedt daarmee ook de route-prefix-lijst van
            // `RestrictedRepairerRouteGuard` (`/intakes` én `/intakes/{nummer}`).
            // 10-10-2026: de tabs `meldingen` en `melding-aannemen` zijn hier
            // WEGGEHAALD — meldingen is een eigen app geworden (zie `meldingen`
            // hieronder). Eén ingang, en de werkvoorraad staat niet langer
            // tussen de dossier-schermen van Glas.
            // S-C (2026-08-21, B1): Planbord vervalt voor de beperkte hersteller
            // (werkplaatsplanning hoort niet bij declaratie-only).
            { id: 'planbord',     label: 'Planbord',     to: '/planbord',     enabled: true, hiddenWhenRestrictedRepairer: true },
            // Story fe-be-nieuwsberichten (2026-07-11): het Nieuwsberichten-menu is
            // bereikbaar vanuit ZOWEL Glas als Documentatie (echte <Link> via
            // AppTabs → rechtsklik-nieuw-tab werkt). GÉÉN gate — iedereen ziet z'n
            // eigen gescoopte feed; beheer is FE-canManage + BE-gate't.
            { id: 'nieuwsberichten', label: 'Nieuwsberichten', to: '/nieuwsberichten', enabled: true },
            // Story fe-relatiebeheer-fase1-A (2026-06-23): de Glas-tabs "Klanten"
            // + "Verzekeraars" zijn VERWIJDERD. "Klanten" verhuist naar de eigen
            // app `relatiebeheer` (zie hieronder); "Verzekeraars" vervalt (legacy,
            // komt later via een aparte functie). Precies één Klanten-ingang.
        ],
    },
    // Story be-fe-callcenter-melding-aanmaken (10-10-2026): meldingen is een
    // EIGEN app geworden, direct achter Glas — een melding wordt een dossier,
    // dus die twee horen naast elkaar.
    //
    // ⚠ De app draagt `requiresIntakesEnabled` op APP-niveau, niet alleen op de
    //   tab. De app-switcher kijkt uitsluitend naar app-velden; zonder deze
    //   poort zou de tegel bij iedereen in beeld staan — ook op instanties
    //   zonder callcenter-instroom — en naar een scherm met een lege tabstrip
    //   leiden.
    //
    // ⚠ `hiddenWhenRestrictedRepairer` op app-niveau blokkeert via
    //   `restrictedRepairerRoutes` het hele eerste padsegment `/intakes`.
    //   Dat is RUIMER dan voorheen (toen stonden alleen de twee tabpaden dicht
    //   en was `/intakes/{nummer}` bereikbaar) en is hier de bedoeling: een
    //   verzekeraar-gebonden hersteller krijgt zijn werk via de vaste
    //   verzekeraar en heeft in de meldingen-instroom niets te zoeken.
    {
        id: 'meldingen',
        label: 'Meldingen',
        icon: PhoneCall,
        defaultRoute: '/intakes',
        enabled: true,
        hiddenWhenRestrictedRepairer: true,
        requiresIntakesEnabled: true,
        tabs: [
            // Eén tab. Aanmaken gebeurt met de knop op de lijst, niet via een
            // eigen tab: zo is er één ingang en zie je meteen in welke
            // werkvoorraad de nieuwe melding terechtkomt.
            { id: 'meldingen', label: 'Meldingen', to: '/intakes', enabled: true },
        ],
    },
    {
        id: 'inrichting',
        label: 'Inrichting',
        icon: Cog,
        // Verhuizing 2026-06-24: "Artikeltypes" + "Prijslijsten" zijn uit
        // Inrichting verwijderd en hebben hun eigen app "Artikelbeheer"
        // gekregen. Default-route is daarom nu Fotoprotocollen.
        defaultRoute: '/inrichting/fotoprotocollen',
        enabled: true,
        // S-C (2026-08-21, beslissing B1): weg voor de beperkte hersteller.
        hiddenWhenRestrictedRepairer: true,
        tabs: [
            { id: 'fotoprotocollen', label: 'Fotoprotocollen', to: '/inrichting/fotoprotocollen', enabled: true },
            // Story fe-be-vervangend-vervoer-voertuigen (2026-06-29): beheer van de
            // voertuigen (UA_RentalCar) die als vervangend vervoer worden meegegeven.
            // GÉÉN rol-gate — spiegelt Leveranciers (BE company-/hersteller-scoping).
            { id: 'vervangend-vervoer', label: 'Vervangend vervoer', to: '/inrichting/vervangend-vervoer', enabled: true },
            // Story fe-monteurs-beheer-crud (2026-07-01, story 1): eigen
            // "Monteurs"-scherm (UA_Fitter-CRUD + optioneel gebruiker koppelen).
            // GÉÉN rol-gate — spiegelt Vervangend vervoer (BE company-scoping).
            { id: 'monteurs', label: 'Monteurs', to: '/inrichting/monteurs', enabled: true },
            // Story fe-be-monteur-s6-onderdelen (S021.6 deel C, PB-15): de checklist "gebruikte
            // onderdelen" van de EIGEN hersteller. Alleen voor een hersteller met een hersteller-id
            // (BE 403 no_repairer_context); system gebruikt "Inloggen als". Geen keten-gate.
            { id: 'onderdelen', label: 'Onderdelenlijst', to: '/inrichting/onderdelen', enabled: true, requiresRepairerContext: true },
            // Story be-fe-eigen-dossiersoorten: de eigen dossiersoorten (UA_CustomDossierType) van de
            // EIGEN vestiging. Zonder vestiging BE 403 no_repairer_context; system via "Inloggen als".
            { id: 'eigen-dossiersoorten', label: 'Eigen dossiersoorten', to: '/inrichting/eigen-dossiersoorten', enabled: true, requiresRepairerContext: true },
            // Story be-fe-templates-beheer: mail-, sms- en WhatsApp-templates van de EIGEN vestiging. Zonder
            // vestiging BE 403 no_repairer_context; system via "Inloggen als".
            { id: 'templates', label: 'Templates', to: '/inrichting/templates', enabled: true, requiresRepairerContext: true },
            { id: 'controleredenen', label: 'Controleredenen', to: '/inrichting/controleredenen', enabled: true, requiresAnyRole: ['glasadmin', 'hoofdkantoor'] },
            // Story fe-be-dossierdocumenten-story1: verzendregels-beslistabel per
            // partij. GÉÉN rol-gate — company-scoped inrichting (BE dwingt de
            // sessie-company af), spiegelt Monteurs/Vervangend vervoer.
            { id: 'verzendregels', label: 'Verzendregels', to: '/inrichting/verzendregels', enabled: true },
            // Story be-fe-standaarddocumenten (G2, PB-S1): de standaardlijst voor de hele instantie (bron 3 van het
            // voorstel op het dossier). Alleen System: de tab verschijnt alleen voor System, de route hangt aan
            // <ProtectedRoute systemOnly> en het endpoint toetst zelf opnieuw (403 role_not_allowed).
            {
                id: 'defaultDocuments',
                label: 'Standaarddocumenten',
                to: '/inrichting/standaarddocumenten',
                enabled: true,
                requiresSystemContext: true,
            },
        ],
    },
    // Story fe-be-bestellijst-overzicht (S-INK.1, 2026-06-18): de app "Inkoop"
    // is enabled met de Bestellijst-tab als eerste/default-route. De overige
    // Inkoop-tabs (Orders/Retouren/Voorraad) blijven disabled ("Binnenkort").
    {
        id: 'inkoop',
        label: 'Inkoop',
        icon: ShoppingCart,
        defaultRoute: '/inkoop/bestellijst',
        enabled: true,
        // S-C (2026-08-21, beslissing B1): weg voor de beperkte hersteller. Sinds story
        // be-fe-instellingen-fundering-inkoop-uit (EPIC-019 K1) zijn de inkoop-endpoints voor
        // hem ook server-side dicht (`PurchasingFeatureGate`).
        hiddenWhenRestrictedRepairer: true,
        tabs: [
            { id: 'bestellijst',  label: 'Bestellijst',  to: '/inkoop/bestellijst',  enabled: true  },
            // Story fe-be-inkoop-leveranciers-beheer (2026-06-19): leverancier-beheer
            // naast Bestellijst. GÉÉN rol-gate — spiegelt Prijslijsten/Artikeltypes:
            // open voor elke ingelogde user, veiligheid via BE company-scoping
            // (SuppliersController = [SessionAuth] + company-scope, geen rol-check),
            // zodat herstellers hun eigen leveranciers beheren.
            { id: 'leveranciers', label: 'Leveranciers', to: '/inkoop/leveranciers', enabled: true },
            // Story fe-be-inkoop-orders-zoeken-inzien (S-INK.3, 2026-06-21):
            // Orders-tab enabled (zoeken + read-only inzien). Retouren/Voorraad
            // blijven disabled ("Binnenkort").
            { id: 'orders',       label: 'Orders',       to: '/inkoop/orders',       enabled: true  },
            { id: 'retouren',     label: 'Retouren',     to: '/inkoop/retouren',     enabled: false },
            // EPIC-022 S022.1: alleen klikbaar bij whoami `inventoryInCarculate` (voorraad in CARculate); anders "Binnenkort".
            { id: 'voorraad',     label: 'Voorraad',     to: '/inkoop/voorraad',     enabled: false, enabledWhenInventoryInCarculate: true },
        ],
    },
    // Onderstaande apps zijn V1-disabled. Geen tabs nodig: de switcher
    // popover toont ze met "Binnenkort"-label en blokkeert klik via
    // `pointer-events: none`.
    // Story fe-relatiebeheer-fase1-A (2026-06-23, OP-1 RESOLVED): de voorheen
    // disabled losse app `klanten-app` is omgezet naar de ENABLED eigen app
    // `relatiebeheer` met de Klanten-ingang als enige tab (defaultRoute /klanten).
    // Eigen switcher-tile (Users-icoon, eigen tab-strip), NIET een Glas-tab. De
    // disabled app `verzekeraars-app` is VERWIJDERD (legacy vervalt; verzekeraars
    // komen later via een aparte functie). Resultaat: precies één Klanten-ingang.
    {
        id: 'relatiebeheer',
        label: 'Relatiebeheer',
        icon: Users,
        defaultRoute: '/klanten',
        enabled: true,
        // 2026-08-23 (PO-bijstelling op B1): alsnog weg voor de beperkte
        // hersteller. Relatie-/verzekeraarbeheer is inrichting, en die groep richt
        // niets in — hij werkt binnen de vaste verzekeraar. Zelfde vlag als de vijf
        // apps van S-C, dus de route-guard (C1) vangt `/klanten` én `/verzekeraars`
        // automatisch af; de klantgegevens op het dossier zelf blijven gewoon
        // zichtbaar (dat is de Klant-tab, geen route van deze app).
        hiddenWhenRestrictedRepairer: true,
        tabs: [
            { id: 'klanten', label: 'Klanten', to: '/klanten', enabled: true },
            // Story fe-relatiebeheer-verzekeraars-fase-a1 (2026-06-24): tweede
            // menu-item "Verzekeraars". GÉÉN rol-gate op de route — bekijken =
            // iedereen (herstellers read-only); de aanmaak-/edit-affordances zijn
            // BE-flag-gestuurd (canManageInsurer/canEditDebtor).
            { id: 'verzekeraars', label: 'Verzekeraars', to: '/verzekeraars', enabled: true },
            // Story be-fe-relatiebeheer-herstellers (2026-10-09, increment A): derde menu-item
            // "Herstellers" — alleen voor System en het keten-hoofdkantoor (whoami
            // `canManageRepairers`). BE blijft de poort.
            { id: 'herstellers', label: 'Herstellers', to: '/herstellers', enabled: true, requiresCanManageRepairers: true },
            // Story be-fe-meerprijs-dealer-kortingen (26-10-2026): de kortingen voor de meerprijs dealer — alleen bij whoami
            // `canManageDealerSurcharge` (vlag aan én System of keten-hoofdkantoor). BE blijft de poort.
            {
                id: 'meerprijs-dealer',
                label: 'Meerprijs dealer',
                to: '/meerprijs-dealer',
                enabled: true,
                requiresCanManageDealerSurcharge: true,
            },
        ],
    },
    // Verhuizing 2026-06-24: de voorheen disabled app `artikelbeheer` is nu een
    // volwaardige ENABLED app met de tabs Artikelen + Artikeltypes + Prijslijsten.
    // De laatste twee zijn uit Inrichting gehaald; "Artikelen" verhuist van het
    // losse `/artikelen`-pad hierheen. Routes onder het prefix `/artikelbeheer/*`
    // (zie routes.tsx + bookmark-redirects). Bewust direct ná `relatiebeheer`
    // geplaatst zodat de enabled apps aaneengesloten in de switcher staan.
    {
        id: 'artikelbeheer',
        label: 'Artikelbeheer',
        icon: Package,
        defaultRoute: '/artikelbeheer/artikelen',
        enabled: true,
        // S-C (2026-08-21, beslissing B1): weg voor de beperkte hersteller.
        hiddenWhenRestrictedRepairer: true,
        tabs: [
            { id: 'artikelen',    label: 'Artikelen',    to: '/artikelbeheer/artikelen',    enabled: true },
            { id: 'artikeltypes', label: 'Artikeltypes', to: '/artikelbeheer/artikeltypes', enabled: true },
            { id: 'prijslijsten', label: 'Prijslijsten', to: '/artikelbeheer/prijslijsten', enabled: true },
        ],
    },
    // Story fe-be-facturatie-export-achtergrond (2026-06-26): de app "Facturatie"
    // is enabled met de Export-tab als eerste/default-route. De "Facturen"-tab
    // (facturen inzien) blijft disabled ("Binnenkort", niet in deze story).
    // GÉÉN rol-gate — spiegelt Leveranciers/Bestellijst: open voor elke ingelogde
    // hersteller-user, veiligheid via BE company-scoping (FacturatieExportController
    // = [SessionAuth] + company-scope).
    {
        id: 'facturatie',
        label: 'Facturatie',
        icon: Receipt,
        // Story fe-be-facturatie-dashboard (2026-07-15): het Dashboard is de nieuwe
        // landingspagina van de Facturatie-app → defaultRoute + eerste tab.
        defaultRoute: '/facturatie/dashboard',
        enabled: true,
        // S-C (2026-08-21, beslissing B1): weg voor de beperkte hersteller. De
        // factuur-INZAGE op het dossier (FacturenDetailPopup) blijft wél —
        // alleen de doorkliklinks naar /facturatie worden platte tekst (C2).
        hiddenWhenRestrictedRepairer: true,
        // Story be-fe-verzekeraar-alleen-lezen-en-facturatie-verbergen (B-4, 22-10-2026): verborgen wanneer
        // facturatie bij de hersteller van de gebruiker uit staat (whoami, gelezen in @/auth/invoicingActive).
        hiddenWhenInvoicingInactive: true,
        tabs: [
            // Story fe-be-facturatie-dashboard (2026-07-15): omzet-per-maand
            // (jaar-over-jaar) + KPI-strip. Eerste tab + defaultRoute. GÉÉN
            // requiresRepairerContext — het dashboard is de default landing, dus een
            // hersteller-only-gate zou een niet-hersteller op een onbereikbare route
            // laten landen. Veiligheid via BE owner-only-scope op
            // CurrentSession.CompanyId. Echte <Link> via AppTabs → rechtsklik-nieuw-tab.
            { id: 'dashboard', label: 'Dashboard', to: '/facturatie/dashboard', enabled: true  },
            { id: 'export',   label: 'Export',   to: '/facturatie/export',   enabled: true  },
            // Story fe-be-facturen-overzicht-journaalregels (2026-07-13): de
            // Facturen-tab (zoekscherm + factuur-detail met journaalregels) is
            // enabled. GÉÉN rol-gate — open voor elke ingelogde hersteller-user,
            // veiligheid via BE company-scoping (InvoicesController = [SessionAuth]
            // + OwnerID-scope / UF_Invoice.CanRead → 404-no-leak).
            { id: 'facturen', label: 'Facturen', to: '/facturatie/facturen', enabled: true  },
            // 10-10-2026: de tab Runs is verhuisd naar de eigen app "Interne facturatie" (hieronder).
            // Story grootboeken-inrichten (fase 1): grootboeken per debiteurengroep
            // inrichten (verkoop-grootboeknummers; geen inkoop). BE company-scoped.
            { id: 'grootboeken', label: 'Grootboeken', to: '/facturatie/grootboeken', enabled: true, requiresRepairerContext: true },
            // Story fe-be-factuurdocumenten-story1 (2026-07-14): READ-ONLY
            // beheerscherm dat de bestaande UF_InvoiceReport-matrix company-gescoped
            // toont (factuurtype × verzekerd × credit). Hersteller-ONLY (PO-keuze
            // 2026-07-14, gespiegeld op Grootboeken): `requiresRepairerContext` →
            // AppTabs verbergt de tab voor System/RegularUser; de route is hard gegate
            // via <ProtectedRoute repairersOnly>. Echte <Link> via AppTabs →
            // rechtsklik-nieuw-tab werkt binnen hersteller-context. BE blijft
            // company-gescoped (InvoiceDocumentsController = [SessionAuth] +
            // OwnerID-scope via UF_InvoiceType).
            { id: 'factuurdocumenten', label: 'Factuurdocumenten', to: '/facturatie/factuurdocumenten', enabled: true, requiresRepairerContext: true },
            // Story exportinstellingen (2026-07-15): het export-blok van legacy
            // Glas/InvoiceSettings (verkoopdagboek, exportroutine, ER/btw-keuzes,
            // omzet-grootboek per btw-tarief). Hersteller-ONLY (de bootstrap-GET
            // zaait UF_CompanyInvoiceSetting-keys): `requiresRepairerContext` →
            // AppTabs verbergt de tab voor System/RegularUser; de route is hard
            // gegate via <ProtectedRoute repairersOnly>. Echte <Link> via AppTabs.
            { id: 'exportinstellingen', label: 'Exportinstellingen', to: '/facturatie/exportinstellingen', enabled: true, requiresRepairerContext: true },
            // Story nummering-factuurreeks-per-jaar (2026-07-15): de Nummering-tab van
            // legacy Glas/InvoiceSettings (start-/volgnummer per reeks) + de opt-in
            // "factuurnummer per jaar laten oplopen". Hersteller-ONLY (de bootstrap-GET
            // zaait UF_CompanyInvoiceSetting-keys): `requiresRepairerContext` → AppTabs
            // verbergt de tab voor System/RegularUser; de route is hard gegate via
            // <ProtectedRoute repairersOnly>. Echte <Link> via AppTabs.
            { id: 'nummering', label: 'Nummering', to: '/facturatie/nummering', enabled: true, requiresRepairerContext: true },
        ],
    },
    // Besluit eigenaar 10-10-2026: de facturatie-runs (controle vooraf, starten, run-detail) zijn een EIGEN app
    // "Interne facturatie", direct ná Facturatie. Het gaat om de facturatie van abonnementen door de eigenaar
    // ervan, niet om de facturen van een hersteller; daarom niet langer een tab tussen diens schermen.
    //
    // ⚠ De poort `requiresBillingRunsAccess` staat op APP-niveau (tegel) én op de tab (strip). Zonder bevestigde
    //   toegang ('allowed') geen tegel en geen tab. De oude URL's `/facturatie/runs…` sturen door (routes.tsx).
    //
    // ⚠ `hiddenWhenRestrictedRepairer` erft de situatie van vóór de verhuizing: de tab hing onder Facturatie, en
    //   die app bestaat niet voor een verzekeraar-gebonden hersteller.
    {
        id: 'interne-facturatie',
        label: 'Interne facturatie',
        icon: Repeat,
        defaultRoute: '/interne-facturatie/runs',
        enabled: true,
        hiddenWhenRestrictedRepairer: true,
        requiresBillingRunsAccess: true,
        tabs: [
            // Story be-fe-facturatie-run-controle (R1): Runs — controle vooraf van een facturatie-run. Alleen
            // zichtbaar voor de eigenaar van abonnementen en system (probe op `options`, geen repairer-gate).
            { id: 'runs', label: 'Runs', to: '/interne-facturatie/runs', enabled: true, requiresBillingRunsAccess: true },
        ],
    },
    // Story fe-be-documentatie-module (2026-07-09): data-gedreven Documentatie-
    // app (vervangt het hardcoded "Documenten"-menu). GÉÉN gate — de tile is
    // zichtbaar en de route bereikbaar voor ELKE ingelogde user (AC1/AC2): de app
    // is read-only voor gewone gebruikers en beheerbaar voor system/keten. De
    // read-only-heid wordt gedreven door `canManage` (system || keten), NIET door
    // tile-zichtbaarheid; de lees-/schrijf-gate wordt BE-side afgedwongen. Eén
    // enkele tab (geen tab-strip nodig) — de boom leeft binnen de pagina. Bewust
    // ná Facturatie geplaatst zodat de artikelbeheer→facturatie-adjacency (geborgd
    // in apps.config.artikelbeheer.test) ongemoeid blijft.
    {
        id: 'documentatie',
        label: 'Documentatie',
        icon: FileText,
        defaultRoute: '/documentatie',
        enabled: true,
        tabs: [
            { id: 'documentatie', label: 'Documentatie', to: '/documentatie', enabled: true },
            // Story fe-be-nieuwsberichten (2026-07-11): het Nieuwsberichten-menu ook
            // vanuit de Documentatie-app bereikbaar (tweede menu-item, echte <Link>).
            { id: 'nieuwsberichten', label: 'Nieuwsberichten', to: '/nieuwsberichten', enabled: true },
        ],
    },
    // Story rapportage-per-rol-fase1 (2026-07-11): de app "Rapportage" is enabled.
    // Elke ingelogde user ziet de tile; de BE bepaalt via precedence welke
    // rapportenboom getoond wordt (rol zonder menu → lege staat). GÉÉN rol-/system-
    // gate op de app zelf (het beheer zit als system-only tab onder Inrichting).
    {
        id: 'rapportage',
        label: 'Rapportage',
        icon: BarChart3,
        defaultRoute: '/rapportage',
        enabled: true,
        // S-C (2026-08-21, beslissing B1): weg voor de beperkte hersteller.
        hiddenWhenRestrictedRepairer: true,
        tabs: [
            { id: 'rapportage', label: 'Rapportage', to: '/rapportage', enabled: true },
        ],
    },
    // Story be-fe-support-app-behandelen (S2, 23-10-2026): de app "Support" — supportmeldingen behandelen, voor
    // EuroMotive (System) en het keten-hoofdkantoor. Zichtbaar alleen bij whoami `canHandleSupport === true`, op app
    // én tab. De melder-routes van S1 (`/support/requests`) horen NIET bij deze app (geen tab): die zijn voor iedereen
    // en blijven ook voor de gebonden hersteller en de verzekeraar bereikbaar. Daarom `sharedFirstSegment` en géén
    // `hiddenWhenRestrictedRepairer`.
    {
        id: 'support',
        label: 'Support',
        icon: LifeBuoy,
        defaultRoute: '/support/inbox',
        enabled: true,
        requiresCanHandleSupport: true,
        sharedFirstSegment: true,
        tabs: [
            { id: 'meldingen', label: 'Meldingen', to: '/support/inbox', enabled: true, requiresCanHandleSupport: true },
            // Story be-fe-support-soorten-foutmelding-monteur (S3, AC 13 / P-S3-7): de soorten inrichten — alleen System
            // (het keten-HK behandelt wel, maar richt niet in). Route achter <ProtectedRoute systemOnly>; de server toetst
            // opnieuw (403 support_types_system_only).
            {
                id: 'soorten',
                label: 'Soorten',
                to: '/support/types',
                enabled: true,
                requiresCanHandleSupport: true,
                requiresSystemContext: true,
            },
        ],
    },
    // Story fe-be-verzekeraar-rol-scope-worklist (2026-06-29): de aparte
    // verzekeraar-app. Alleen zichtbaar voor de verzekeraar-rol
    // (requiresAnyRole) en in de kale chrome rendert ALLEEN deze app z'n tab
    // "Te beoordelen". De app-switcher-tile wordt sowieso niet getoond aan de
    // verzekeraar (kale chrome) en is voor interne users role-gated weg.
    // "Calculatie" (optioneel/uit-scope story 1) is bewust NIET toegevoegd; een
    // tweede tab is triviaal bij te zetten (eigen Tab-entry hieronder).
    //
    // ⚠⚠ SINDS 09-09-2026 HEEFT DE VERZEKERAAR TWEE TEGELS, en daarmee voor het eerst
    //    een echte app-switcher in plaats van een kale schil. Beslissing eigenaar:
    //    "app-switcher met twee tegels: 1e is Glas met daarin wat er nu al is (te
    //    beoordelen en Dossiers), 2e Claimfilter met de oordeelregels".
    //
    //    De eerste heette daarom tot die datum "Te beoordelen" — dezelfde naam als haar
    //    eerste tab. Met twee tegels ernaast werkt dat niet meer: een tegel is het
    //    WERKGEBIED en de tab het scherm, en die twee dezelfde naam geven maakt de
    //    tweede tab ("Dossiers") tot een vreemde eend onder een tegel die zich als één
    //    scherm voordoet.
    {
        id: 'verzekeraar',
        label: 'Glas',
        icon: ShieldCheck,
        defaultRoute: '/te-beoordelen',
        enabled: true,
        requiresAnyRole: [VERZEKERAAR_ROLE],
        tabs: [
            { id: 'te-beoordelen', label: 'Te beoordelen', to: '/te-beoordelen', enabled: true, requiresAnyRole: [VERZEKERAAR_ROLE] },
            // Story fe-be-verzekeraar-dossiers-zoeken (2026-06-30): tweede menu-item
            // "Dossiers" — een volwaardig zoekscherm over ALLE statussen + archief
            // binnen de verzekeraar-scope. Route `/te-beoordelen/dossiers` (child van
            // de insurer-landing-prefix → valt al binnen INSURER_ALLOWED_PREFIXES via
            // startsWith; botst NIET met de hersteller-`/dossiers`-route).
            { id: 'verzekeraar-dossiers', label: 'Dossiers', to: '/te-beoordelen/dossiers', enabled: true, requiresAnyRole: [VERZEKERAAR_ROLE] },
        ],
    },
    // De tweede tegel van de verzekeraar (09-09-2026): haar eigen oordeelsregels.
    //
    // ⚠⚠ DIT IS EEN EIGEN APP EN GEEN TAB ONDER `claimfilter`. Die app draagt de
    //    system/support-poort; er een verzekeraar-tab in hangen zou betekenen dat de
    //    poort van de app en die van de tab uit elkaar lopen — en dan is de app zelf
    //    voor haar onzichtbaar terwijl de tab zegt dat hij er is.
    //
    // ⚠ Het scherm is HETZELFDE als de zesde tab van de Claimfilter-app; alleen de weg
    //   ernaartoe verschilt per publiek. Wat ieder te zien krijgt bepaalt het BACKEND:
    //   `ResolveInsurerIds()` geeft system/support alle maatschappijen en de verzekeraar
    //   uitsluitend die van haar eigen bedrijf.
    //
    // ⚠ Eén tab, en dat is met opzet geen reden om de tegel weg te laten: zij is het
    //   WERKGEBIED, en de volgende claimfilter-schermen voor de maatschappij horen hier
    //   naast te komen in plaats van ergens anders aangehaakt te worden.
    {
        id: 'verzekeraar-claimfilter',
        label: 'Claimfilter',
        icon: Filter,
        defaultRoute: CLAIMFILTER_ROUTES.rules,
        enabled: true,
        requiresAnyRole: [VERZEKERAAR_ROLE],
        tabs: [
            {
                id: 'verzekeraar-oordeelsregels',
                label: 'Oordeelsregels',
                to: CLAIMFILTER_ROUTES.rules,
                enabled: true,
                requiresAnyRole: [VERZEKERAAR_ROLE],
            },
        ],
    },
    // Story fe-be-polis-reply-mailbox-poliscontrole (verhuizing 2026-06-26): de
    // mailbox-beheer-CRUD verhuist van Inrichting naar een eigen app "System"
    // die ALLEEN voor system-gebruikers (`userContextType === 'System'`)
    // zichtbaar én bereikbaar is. De routes zijn hard gegate via
    // `<ProtectedRoute systemOnly>`. Eigen icoon (ServerCog) — niet Cog, dat is
    // Inrichting.
    //
    // ⚠⚠ Story be-fe-systeem-gebruikers (P-2, 24-10-2026): het keten-hoofdkantoor ziet deze tegel ook, met ALLEEN de tab
    //    Gebruikers. De app draagt daarom de OF-poort `requiresSystemContextOrCanManageUsers` (niet meer
    //    `requiresSystemContext`) en ELKE tab draagt zijn eigen poort. Een nieuwe tab zonder poort is rood in
    //    `features/system/users/__tests__/systemUsersApp.test.ts`.
    {
        id: 'system',
        label: 'System',
        icon: ServerCog,
        defaultRoute: '/system/mailboxen',
        enabled: true,
        requiresSystemContextOrCanManageUsers: true,
        tabs: [
            // ⚠ Story fe-tab-groepen-en-meer-menu (24-10-2026): de VOLGORDE van deze lijst is de volgorde van de strip —
            //   Gebruikers · Berichten en mail · Koppelingen · Inrichting (zie `tabGroups` hieronder). Een groep staat op de
            //   plek van haar eerste tab. De id's en labels zijn ongewijzigd: ze zijn de sleutels van Systeem → Menu.
            // Story be-fe-systeem-gebruikers (24-10-2026): gebruikers zoeken en beheren — System (alle gebruikers) en het
            // keten-hoofdkantoor (gebruikers binnen de eigen keten). Stabiele `id` (menu-sleutel `menu.system.users`). De
            // route hangt aan <ProtectedRoute canManageUsers> (niet systemOnly) en elk endpoint toetst zelf.
            { id: 'users', label: 'Gebruikers', to: '/system/users', enabled: true, requiresCanManageUsers: true },
            // Story fe-systeemberichten (2026-06-26): beheer van de globale
            // systeemberichten-banner. System-only. Echte <Link> via AppTabs → nieuw-tab werkt.
            { id: 'berichten', label: 'Systeemberichten', to: '/system/berichten', enabled: true, requiresSystemContext: true },
            { id: 'mailboxen', label: 'Mailboxen', to: '/system/mailboxen', enabled: true, requiresSystemContext: true },
            // Story be-fe-nh1816-mail-intake-declaraties (2026-09-17): het READ-ONLY
            // zicht op wat er via de declaratie-postvakken binnenkomt (K5).
            // ⚠ Zonder dit item is het scherm alleen via de URL te vinden, en dan
            //   kijkt er niemand — precies de week stilstand die K5 moet voorkomen.
            {
                id: 'declaratie-aanleveringen',
                label: 'Declaraties per mail',
                to: '/system/declaratie-aanleveringen',
                enabled: true,
                requiresSystemContext: true,
            },
            // Story be-fe-partner-api-dossier-aanmaken (S020.1, 23-10-2026): de partners van de partner-API. Stabiele
            // `id` (de menu-sleutel `menu.system.partners`); de tab is een <Link> via AppTabs; de route hangt aan
            // <ProtectedRoute systemOnly> en elk endpoint toetst opnieuw (403).
            { id: 'partners', label: 'Partnerkoppelingen', to: '/system/partners', enabled: true, requiresSystemContext: true },
            // Story be-fe-nh1816-partijstructuur-en-aanlevering (2026-09-17): de
            // aanleverende ketens en vestigingen van de mailstroom. De routes
            // zijn hard gegate via <ProtectedRoute systemOnly> én de endpoints
            // doen de toets zelf opnieuw (403).
            {
                id: 'declaration-parties',
                label: 'Aanleverende partijen',
                to: '/system/declaration-parties',
                enabled: true,
                requiresSystemContext: true,
            },
            // Story be-fe-configuratie-overzicht (2026-09-30): het READ-ONLY
            // overzicht van alle instellingen uit Web.config, met per sleutel of
            // hij gevuld is en of hij per omgeving moet verschillen. De route is
            // hard gegate via <ProtectedRoute systemOnly> en het endpoint toetst
            // opnieuw (403).
            { id: 'configuratie', label: 'Configuratie', to: '/system/configuratie', enabled: true, requiresSystemContext: true },
            // Story be-fe-menu-aan-uit (21-10-2026): elke app en elk menu-item van deze lijst aan of uit zetten,
            // voor iedereen of voor één rol (`@/auth/menuVisibility`). De hele System-app is al
            // requiresSystemContext; de tab draagt het daarnaast zelf, de route hangt aan <ProtectedRoute systemOnly>
            // en elk endpoint toetst opnieuw (403).
            { id: 'menu', label: 'Menu', to: '/system/menu', enabled: true, requiresSystemContext: true },
            // Story rapportage-per-rol-fase1 (2026-07-11): beheerscherm A
            // "Rapportage-menu per rol" — verplaatst van Inrichting naar System
            // (2026-07-13). Route hard gegate via <ProtectedRoute systemOnly>.
            {
                id: 'rapportage-menu',
                label: 'Rapportage-menu per rol',
                to: '/system/rapportage-menu',
                enabled: true,
                requiresSystemContext: true,
            },
            // Story be-fe-systeem-logboek (25-10-2026): de logregels van AppServices zoeken en lezen (alleen System, ook
            // niet het keten-HK). Stabiele `id` (menu-sleutel `menu.system.log`). In de groep Inrichting, naast
            // Configuratie (beide diagnostiek), zodat de besloten strip van vier items blijft. De route hangt aan
            // <ProtectedRoute systemOnly> en elk endpoint toetst opnieuw (403).
            { id: 'log', label: 'Logboek', to: '/system/log', enabled: true, requiresSystemContext: true },
        ],
        // Story fe-tab-groepen-en-meer-menu (24-10-2026, besluit gebruiker): de strip van System was te breed. Elke tab houdt
        // zijn eigen poort; een groep zonder zichtbare tab verdwijnt en een groep met één zichtbare tab is een gewone tab
        // (het keten-HK met alleen `canManageUsers` ziet dus alleen "Gebruikers").
        tabGroups: [
            { id: 'messages-and-mail', label: 'Berichten en mail', tabIds: ['berichten', 'mailboxen', 'declaratie-aanleveringen'] },
            { id: 'integrations', label: 'Koppelingen', tabIds: ['partners', 'declaration-parties'] },
            { id: 'setup', label: 'Inrichting', tabIds: ['configuratie', 'menu', 'rapportage-menu', 'log'] },
        ],
    },
    // Story fe-be-claimfilter-beheerscherm (2026-08-31): zien wat de
    // claimfilter-keten doet, zonder in een traclog van 198 MB te graven.
    //
    // ⚠ BEWUST EEN EIGEN TEGEL EN GEEN TAB ONDER `system`: het publiek is
    //   BREDER dan system (system ÉN support/helpdesk — story §4.2), en een tab
    //   in een app die support niet ziet is geen oplossing maar een verstopplek.
    //
    // ⚠⚠ DE POORT COMPONEERT MET **OF**, EN DAAROM STAAT HIER
    //    `requiresSystemContextOrAnyRole` EN NIET `requiresSystemContext` +
    //    `requiresAnyRole`. Die twee worden met && samengevoegd; samen gezet
    //    leveren ze een tegel op die voor NIEMAND zichtbaar is — zonder
    //    foutmelding, en dus niet te onderscheiden van "nog niet gebouwd"
    //    (story §4.3, AC-5.1).
    //
    // ⚠ De vijf tabs dragen GEEN eigen gate: zij erven de app-poort (AC-5.2).
    //   Een gate per tab zou dezelfde EN-val vijf keer één niveau lager
    //   herhalen, en dan is de tegel wél zichtbaar maar zijn de tabs leeg.
    //
    // ⚠⚠ Dat geldt ONVERKORT voor de vijfde tab (Claimfilters, 2026-08-31): die
    //    toont de gevoeligste inhoud van het scherm en dat is juist GÉÉN reden
    //    voor een eigen gate. Een tweede poort naast de app-poort is een tweede
    //    plek waar de OF stil een EN kan worden. De inhoud wordt beschermd door
    //    de poort die er al is, plus AC-56 (story §4.3).
    {
        id: 'claimfilter',
        label: 'Claimfilter',
        icon: Filter,
        defaultRoute: CLAIMFILTER_ROUTES.overview,
        enabled: true,
        requiresSystemContextOrAnyRole: CLAIMFILTER_AUDIENCE_ROLES,
        // ⚠ Claimfilters staat TUSSEN Dossiers en Gezondheid, niet erachter:
        //   Overzicht/Rondes/Dossiers/Claimfilters gaan over het werk,
        //   Gezondheid is de staande controle en hoort als laatste (§4.1).
        tabs: [
            { id: 'claimfilter-overzicht',    label: 'Overzicht',    to: CLAIMFILTER_ROUTES.overview,     enabled: true },
            { id: 'claimfilter-rondes',       label: 'Rondes',       to: CLAIMFILTER_ROUTES.rounds,       enabled: true },
            { id: 'claimfilter-dossiers',     label: 'Dossiers',     to: CLAIMFILTER_ROUTES.dossiers,     enabled: true },
            { id: 'claimfilter-claimfilters', label: 'Claimfilters', to: CLAIMFILTER_ROUTES.claimFilters, enabled: true },
            // ⚠ De ZESDE tab (04-09-2026) draagt — net als de vijfde — GÉÉN eigen gate.
            //   Hij erft de app-poort, en dat is precies de bedoeling: een tweede poort
            //   naast de app-poort is een tweede plek waar de OF stil een EN kan worden.
            { id: 'claimfilter-oordeelsregels', label: 'Oordeelsregels', to: CLAIMFILTER_ROUTES.rules, enabled: true },
            { id: 'claimfilter-gezondheid',   label: 'Gezondheid',   to: CLAIMFILTER_ROUTES.health,       enabled: true },
        ],
    },
    // Story fe-be-cursusmodus-hersteller-mvp (AC-C1.1): de app "Cursussen" bestaat alleen op een
    // ACTIEVE trainingsomgeving en alleen voor een hersteller-vestiging (`canUseCourses`). Op
    // productie staat de sleutel `training` niet in whoami en is de tegel er nooit. De route
    // `/cursussen` heeft daarnaast een eigen poort in `routes.tsx` (AC-C1.2).
    {
        id: 'cursussen',
        label: 'Cursussen',
        icon: GraduationCap,
        defaultRoute: '/cursussen',
        enabled: true,
        requiresCourses: true,
        tabs: [{ id: 'cursussen', label: 'Cursussen', to: '/cursussen', enabled: true }],
    },
];

/**
 * Bepaalt welke app actief is op basis van de huidige pathname. Loopt
 * alleen door enabled-apps — disabled apps kunnen nooit "active" zijn
 * omdat ze geen routes hebben.
 *
 * Fallback bij geen match: eerste enabled app (Glas). Dat is praktisch
 * voor routes als `/dev/tokens-preview` (chrome wordt daar niet
 * gerenderd, maar de helper is defensief).
 */
export function findActiveApp(pathname: string): App {
    const match = APPS.find(
        (app) => app.enabled && app.tabs.some((tab) => pathname.startsWith(tab.to)),
    );
    // APPS is een literal const-array met minimaal 1 entry (Glas); de
    // `as App`-assertion is veilig en spaart een `if (!app[0]) throw`
    // gymnastiek bij noUncheckedIndexedAccess.
    return match ?? (APPS[0] as App);
}

/**
 * Vindt binnen een gegeven app de tab waarvan de `to`-prefix matcht.
 * `undefined` wanneer geen tab matcht (bv. wanneer de app geen tabs heeft
 * of de route in een gap valt).
 *
 * Gebruikt `startsWith` zodat detail-routes (`/dossiers/:id`,
 * `/artikelbeheer/prijslijsten/:id/bewerken`) de parent-tab actief houden.
 *
 * **Langste-prefix-wint** (story fe-be-verzekeraar-dossiers-zoeken, 2026-06-30):
 * wanneer twee tabs geneste prefixes hebben (bv. `/te-beoordelen` en
 * `/te-beoordelen/dossiers`) matcht `startsWith` op béíde. We kiezen dan de tab
 * met de langste matchende `to` zodat de meest-specifieke (de child-route)
 * actief is i.p.v. de eerste in de array. Voor niet-geneste tabs is dit
 * functioneel identiek aan de oude `find`-volgorde.
 */
export function findActiveTab(app: App, pathname: string): Tab | undefined {
    let best: Tab | undefined;
    for (const tab of app.tabs) {
        if (pathname.startsWith(tab.to) && (best === undefined || tab.to.length > best.to.length)) {
            best = tab;
        }
    }
    return best;
}
